// 施工実施画面群共通ヘルパー。
// 名前ルックアップ・進捗判定・日付フォーマット。

import { ExtendedDB } from "../../../types/extendedDB";
import { ConstructionPlanAreaBase } from "../../../schema/generated/ConstructionPlanArea";
import { formatDate as formatDateShared, formatDateTime as formatDateTimeShared, formatHMS as formatHMSShared } from "../../../util/dateFormat";

// 一時停止はサーバ列に持たない(mock 同様クライアント状態)。pausedTotalMs=累積停止時間, pausedAt=停止中なら停止開始時刻。
export type TimerClientState = { pausedTotalMs: number; pausedAt: number | null };
export type PauseMap = Record<string, TimerClientState>;

// 経過秒。finished は確定、running は now まで、paused は pausedAt まで。累積停止時間を差し引く。
export function computeElapsedSec(startedAt: number | null, finishedAt: number | null, pause: TimerClientState | undefined, now: number): number {
  if (startedAt == null) {
    return 0;
  }
  const pausedTotal = pause != null ? pause.pausedTotalMs : 0;
  const pausedAt = pause != null ? pause.pausedAt : null;
  const end = finishedAt != null ? finishedAt : (pausedAt != null ? pausedAt : now);
  return Math.max(0, Math.floor((end - startedAt - pausedTotal) / 1000));
}

// 工程(work_detail)の配合比と主材料を解決する。配合比未設定は null。
export function mixInfoForWorkDetail(db: ExtendedDB, workDetailId: number | null) {
  if (workDetailId == null) {
    return null;
  }
  const wd = db.workDetails.find((w) => w.id === workDetailId);
  if (wd == null || wd.mix_ratio_definition_id == null) {
    return null;
  }
  const mixDefId = wd.mix_ratio_definition_id;
  const mix = db.mixRatioDefinitions.find((m) => m.id === mixDefId && m.deleted_at == null);
  if (mix == null) {
    return null;
  }
  const mainMaterialId = mix.material_id;
  const mainMaterial = db.materials.find((m) => m.id === mainMaterialId) ?? null;
  return { workDetail: wd, mix, mainMaterial };
}

// 工程が使う材料名一覧(配合比明細から)。
export function materialNamesForWorkDetail(db: ExtendedDB, workDetailId: number | null): string[] {
  const info = mixInfoForWorkDetail(db, workDetailId);
  if (info == null) {
    return [];
  }
  const names = new Set<string>();
  const mixId = info.mix.id;
  for (const d of db.mixRatioDetails.filter((x) => x.deleted_at == null && x.mix_ratio_definition_id === mixId)) {
    const md = db.materialDetails.find((x) => x.id === d.material_detail_id);
    if (md == null) {
      continue;
    }
    const matId = md.material_id;
    const mat = db.materials.find((m) => m.id === matId);
    if (mat != null) {
      names.add(mat.name);
    }
  }
  return Array.from(names);
}

// 施工箇所の下書き状況(finished_at ベース。null = 下書き)。util/areaStatus.ts の完了ベース AreaStatus とは別軸のため改名して区別する。
//   pending     = 工程実施が1件も無い
//   in_progress = 未完了のまま保存(finished_at が null)の施工実施が存在する
//   done        = 紐づく施工実施がすべて完了保存(finished_at が 非null)
export type AreaDraftStatus = "pending" | "in_progress" | "done";

export function getAreaDraftStatus(area: ConstructionPlanAreaBase, db: ExtendedDB): AreaDraftStatus {
  const pes = db.processExecutions.filter((p) => p.construction_plan_area_id === area.id && p.deleted_at == null);
  if (pes.length === 0) {
    return "pending";
  }
  const conIds = new Set(pes.map((p) => p.construction_id));
  const hasDraft = db.constructions.some((c) => conIds.has(c.id) && c.finished_at == null && c.deleted_at == null);
  if (hasDraft) {
    return "in_progress";
  }
  return "done";
}

// 施工箇所に紐づく未完了(下書き)の施工実施 id。「施工を再開」の遷移先。
export function getAreaDraftConstructionId(area: ConstructionPlanAreaBase, db: ExtendedDB): string | null {
  const pes = db.processExecutions.filter((p) => p.construction_plan_area_id === area.id && p.deleted_at == null);
  for (const pe of pes) {
    const con = db.constructions.find((c) => c.id === pe.construction_id && c.finished_at == null && c.deleted_at == null);
    if (con != null) {
      return con.id;
    }
  }
  return null;
}

export function formatHMS(seconds: number | null | undefined): string {
  return formatHMSShared(seconds);
}

export function formatDate(ts: number | null | undefined): string {
  return formatDateShared(ts);
}

export function formatDateTime(ts: number | null | undefined): string {
  return formatDateTimeShared(ts);
}

export function workSubtypeName(db: ExtendedDB, id: number | null | undefined): string {
  if (id == null) return "-";
  return db.workSubtypes.find((w) => w.id === id)?.name ?? "?";
}

export function workDetailName(db: ExtendedDB, id: number | null | undefined): string {
  if (id == null) return "-";
  return db.workDetails.find((w) => w.id === id)?.name ?? "?";
}

export function workName(db: ExtendedDB, id: number | null | undefined): string {
  if (id == null) return "-";
  const w = db.works.find((x) => x.id === id);
  return w?.short_name ?? w?.official_name ?? "?";
}

export function areaLabel(db: ExtendedDB, areaId: string | null | undefined): string {
  if (areaId == null) return "-";
  const a = db.constructionPlanAreas.find((x) => x.id === areaId);
  if (!a) return "?";
  const parts = [a.area_info_1, a.area_info_2, a.area_info_3, a.area_info_4, a.area_info_5].filter((p) => p && p.length > 0);
  if (parts.length === 0) return `エリア ${a.id.slice(0, 8)}`;
  return parts.join(" / ");
}

// ===== 工程実施 追加ダイアログ: 候補生成(純関数, A-4-3) =====

export type ProcessExecutionCandidate = {
  areaId: string;
  workDetailId: number;
  workTypeId: number | null;
  workSubtypeId: number | null;
  locationPath: string;
  alreadyExists: boolean;
};

type ExistingPeLike = { construction_plan_area_id: string; work_detail_id: number; deleted_at: number | null };

// 工程実施追加ダイアログの候補(施工箇所 × 工程)を生成する。
//   - 既存 (areaId, workDetailId) は候補から除外する(alreadyExists は常に false)
//   - planId 指定時はその plan の施工箇所のみ。未指定時は全施工箇所が対象
//   - 施工箇所配下の全工程(work_subtype 配下の workDetails)との直積を candidate とする
export function buildProcessExecutionCandidates(
  db: ExtendedDB,
  planId: string,
  existingPes: ExistingPeLike[],
): ProcessExecutionCandidate[] {
  const existingKeys = new Set(
    existingPes.filter((p) => p.deleted_at == null).map((p) => `${p.construction_plan_area_id}|${p.work_detail_id}`)
  );
  const areas = db.constructionPlanAreas.filter((a) => a.deleted_at == null && (planId === "" || a.construction_plan_id === planId));
  const candidates: ProcessExecutionCandidate[] = [];
  for (const area of areas) {
    const hasWorkSubtype = area.work_subtype_id != null;
    if (hasWorkSubtype) {
      const wds = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === area.work_subtype_id);
      for (const wd of wds) {
        const key = `${area.id}|${wd.id}`;
        const isNewCandidate = !existingKeys.has(key);
        if (isNewCandidate) {
          candidates.push({
            areaId: area.id,
            workDetailId: wd.id,
            workTypeId: area.work_type_id,
            workSubtypeId: area.work_subtype_id,
            locationPath: areaLabel(db, area.id),
            alreadyExists: false,
          });
        }
      }
    }
  }
  return candidates;
}

